import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { MapPin, MapTrifold, Plus, Trash, X } from '@phosphor-icons/react';
import { bihinApi, fileUrl } from './bihinApi';

// `access` はふだんレイアウトから（Outlet 経由で）来る。まとめ画面
// （フロアマップのタブ）から呼ばれたときだけ props で渡ってくる。
export default function BihinFloormap({ access: accessProp }) {
  const ctx = useOutletContext() || {};
  const access = accessProp || ctx.access;
  const canEdit = !!access?.can_edit;
  const navigate = useNavigate();
  const [maps, setMaps] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [placingItemId, setPlacingItemId] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [m, it] = await Promise.all([bihinApi.floormaps(), bihinApi.items({})]);
      setMaps(m);
      setItems(it);
      setSelectedMapId((prev) => prev && m.some((x) => x.id === prev) ? prev : (m[0]?.id || null));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedMap = maps.find((m) => m.id === selectedMapId) || null;
  const pinned = useMemo(
    () => items.filter((it) => it.floormap_id === selectedMapId && it.pin_x != null),
    [items, selectedMapId],
  );

  const createMap = async () => {
    if (!newName.trim() || !newFile) { toast.error('名前と画像を指定してください'); return; }
    setBusy(true);
    try {
      await bihinApi.createFloormap(newName.trim(), newFile);
      toast.success('フロアマップを追加しました');
      setNewOpen(false); setNewName(''); setNewFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '追加に失敗しました');
    } finally { setBusy(false); }
  };

  const deleteMap = async () => {
    if (!selectedMap) return;
    if (!window.confirm(`フロアマップ「${selectedMap.name}」を削除しますか？配置済みのピンも解除されます。`)) return;
    try {
      await bihinApi.deleteFloormap(selectedMap.id);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  const onMapClick = async (e) => {
    if (!canEdit || !placingItemId || !imgRef.current || !selectedMapId) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    try {
      await bihinApi.setPin(placingItemId, { floormap_id: selectedMapId, pin_x: Math.round(x * 10) / 10, pin_y: Math.round(y * 10) / 10 });
      toast.success('ピンを配置しました');
      setPlacingItemId('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'ピン配置に失敗しました');
    }
  };

  const removePin = async (itemId) => {
    try {
      await bihinApi.setPin(itemId, { floormap_id: null });
      toast.success('ピンを解除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ピン解除に失敗しました');
    }
  };

  return (
    <div className="space-y-4" data-testid="bihin-floormap-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">フロアマップ</h1>
          <p className="text-xs text-zinc-500 mt-0.5">備品の設置場所（電球・消火器など）を地図上のピンで管理します。</p>
        </div>
        {canEdit && (
          <Button onClick={() => setNewOpen(true)} className="bg-teal-700 hover:bg-teal-800" data-testid="bihin-floormap-new-btn">
            <Plus size={15} /><span className="ml-1">マップを追加</span>
          </Button>
        )}
      </div>

      {maps.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-green-200 bg-green-50/30 p-10 text-center">
          <MapTrifold size={32} weight="duotone" className="mx-auto text-green-500 mb-2" />
          <div className="text-sm text-zinc-600">フロアマップがまだありません。<br />
            <span className="text-xs text-zinc-500">オフィスの見取り図（PNG/JPG）をアップロードして、備品の位置をピンで管理できます。</span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="bihin-floormap-tabs">
            {maps.map((m) => (
              <button key={m.id} type="button" onClick={() => setSelectedMapId(m.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  selectedMapId === m.id ? 'bg-green-600 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
                data-testid={`bihin-floormap-tab-${m.id}`}>
                {m.name}
              </button>
            ))}
            {canEdit && selectedMap && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-rose-500 hover:bg-rose-50" onClick={deleteMap} data-testid="bihin-floormap-delete-btn">
                <Trash size={13} />
              </Button>
            )}
          </div>

          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap rounded-lg border border-green-200 bg-green-50/40 px-3 py-2" data-testid="bihin-pin-toolbar">
              <MapPin size={15} weight="duotone" className="text-green-600 shrink-0" />
              <span className="text-xs text-zinc-600 shrink-0">ピンを配置:</span>
              <div className="flex-1 min-w-52 max-w-sm">
                <SearchableSelect
                  value={placingItemId}
                  onValueChange={setPlacingItemId}
                  placeholder="備品を選択…"
                  searchPlaceholder="名前・品番で検索"
                  testid="bihin-pin-item-select"
                  options={items.map((it) => ({
                    value: it.id,
                    primary: it.name,
                    secondary: `${it.asset_no || ''}${it.floormap_id ? ' (配置済)' : ''}`,
                  }))}
                />
              </div>
              {placingItemId ? (
                <span className="text-xs font-semibold text-green-700">マップ上をクリックして配置してください</span>
              ) : (
                <span className="text-[11px] text-zinc-400">備品を選ぶ → マップをクリック</span>
              )}
            </div>
          )}

          {selectedMap && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
              <div className={`relative rounded-xl border border-zinc-200 overflow-hidden bg-zinc-50 ${placingItemId ? 'cursor-crosshair ring-2 ring-green-400' : ''}`}
                data-testid="bihin-floormap-canvas">
                <img ref={imgRef} src={fileUrl(selectedMap.image_url)} alt={selectedMap.name}
                  className="w-full select-none" onClick={onMapClick} draggable={false} />
                {pinned.map((it) => (
                  <button key={it.id} type="button"
                    className="absolute -translate-x-1/2 -translate-y-full group"
                    style={{ left: `${it.pin_x}%`, top: `${it.pin_y}%` }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/bihin/items?open=${it.id}`); }}
                    title={it.name}
                    data-testid={`bihin-pin-${it.id}`}>
                    <MapPin size={26} weight="fill" className="text-green-600 drop-shadow-md group-hover:text-green-500 transition-colors" />
                    <span className="absolute left-1/2 -translate-x-1/2 -top-6 whitespace-nowrap rounded-md bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {it.name}
                    </span>
                  </button>
                ))}
              </div>

              <div className="space-y-2" data-testid="bihin-pin-list">
                <div className="text-xs font-semibold text-zinc-600">このマップの備品 ({pinned.length})</div>
                {pinned.length === 0 ? (
                  <div className="text-[11px] text-zinc-400">まだピンがありません</div>
                ) : (
                  pinned.map((it) => (
                    <div key={it.id} className="flex items-center justify-between rounded-lg border border-zinc-100 px-2.5 py-1.5 bg-white">
                      <button type="button" className="text-xs font-semibold text-zinc-700 hover:text-green-700 truncate text-left"
                        onClick={() => navigate(`/bihin/items?open=${it.id}`)}
                        data-testid={`bihin-pin-list-item-${it.id}`}>
                        {it.name}
                      </button>
                      {canEdit && (
                        <button type="button" className="text-zinc-400 hover:text-rose-500 shrink-0 ml-1"
                          title="ピンを解除" onClick={() => removePin(it.id)}
                          data-testid={`bihin-pin-remove-${it.id}`}>
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-sm" data-testid="bihin-floormap-new-dialog">
          <DialogHeader><DialogTitle>フロアマップを追加</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">名前 *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="例: 本社 2F" className="h-10 mt-1" data-testid="bihin-floormap-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">見取り図画像 (PNG/JPG) *</Label>
              <input type="file" accept="image/*" className="mt-1 block w-full text-xs text-zinc-600 file:mr-2 file:rounded-md file:border-0 file:bg-green-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-green-700 hover:file:bg-green-100"
                onChange={(e) => setNewFile(e.target.files?.[0] || null)} data-testid="bihin-floormap-file-input" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setNewOpen(false)}>キャンセル</Button>
              <Button onClick={createMap} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="bihin-floormap-create-btn">追加する</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
